import { defineStore } from 'pinia';

/**
 * Maximum number of search history entries to keep
 */
const MAX_HISTORY_ENTRIES = 5;

/**
 * Interface for search history entries
 */
export interface SearchHistoryEntry {
  id: string;
  text: string;
  timestamp: number; // Unix timestamp in milliseconds
}

/**
 * Store for managing search history
 */
export const useSearchHistoryStore = defineStore('searchHistory', {
  state: () => ({
    searchHistory: [] as SearchHistoryEntry[]
  }),

  getters: {
    /**
     * Get the most recent search history entries (limited to MAX_HISTORY_ENTRIES)
     */
    recentSearches: (state) => {
      return [...state.searchHistory]
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, MAX_HISTORY_ENTRIES);
    }
  },

  actions: {
    /**
     * Add a new entry to the search history
     * @param text The search text to add
     */
    addSearchEntry(text: string) {
      // Don't add empty searches
      if (!text.trim()) {
        return;
      }

      // Check if this search text already exists (case insensitive)
      const normalizedText = text.trim().toLowerCase();
      const existingIndex = this.searchHistory.findIndex(
        (entry) => entry.text.toLowerCase() === normalizedText
      );

      // If it exists, remove it (we'll add it again as the most recent)
      if (existingIndex !== -1) {
        this.searchHistory.splice(existingIndex, 1);
      }

      // Create a new entry with unique ID and current timestamp
      const now = Date.now();
      const newEntry: SearchHistoryEntry = {
        id: `search_${now}_${Math.random().toString(36).substring(2, 9)}`,
        text: text.trim(),
        timestamp: now
      };

      // Add to search history
      this.searchHistory.push(newEntry);

      // If we have more than the maximum allowed entries, remove the oldest one(s)
      if (this.searchHistory.length > MAX_HISTORY_ENTRIES) {
        // Sort by timestamp (oldest first)
        const sortedHistory = [...this.searchHistory].sort(
          (a, b) => a.timestamp - b.timestamp
        );

        // Get IDs of entries to remove
        const entriesToRemove = sortedHistory
          .slice(0, this.searchHistory.length - MAX_HISTORY_ENTRIES)
          .map((entry) => entry.id);

        // Remove the oldest entries
        this.searchHistory = this.searchHistory.filter(
          (entry) => !entriesToRemove.includes(entry.id)
        );
      }
    },

    /**
     * Remove a search entry by ID
     * @param id The ID of the entry to remove
     */
    removeSearchEntry(id: string) {
      this.searchHistory = this.searchHistory.filter(
        (entry) => entry.id !== id
      );
    },

    /**
     * Clear all search history
     */
    clearSearchHistory() {
      this.searchHistory = [];
    }
  },
  // Simple persistence configuration - no custom serializer needed since we're using numeric timestamps
  persist: true
});
